import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Crown, UserCheck, TrendingUp } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { logUpdate } from '@/lib/audit-log';

export default function Dashboard() {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [pendingProChange, setPendingProChange] = useState<boolean | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const [total, pro, free, recent] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('is_pro', true),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('is_pro', false),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', subDays(new Date(), 7).toISOString()),
        // supabase.from('challenge_submissions').select('*', { count: 'exact', head: true }),
      ]);
      return {
        total: total.count ?? 0,
        pro: pro.count ?? 0,
        free: free.count ?? 0,
        recent: recent.count ?? 0,
        // submissions: submissions.count ?? 0,
      };
    },
  });

  const { data: recentUsers, isLoading } = useQuery({
    queryKey: ['recent-users'],
    queryFn: async () => {
      const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false }).limit(10);
      return data ?? [];
    },
  });

  // const { data: userSubmissions } = useQuery({
  //   queryKey: ['dashboard-user-submissions', selectedUser?.id],
  //   enabled: !!selectedUser?.id,
  //   queryFn: async () => {
  //     const { data } = await supabase
  //       .from('challenge_submissions')
  //       .select('*')
  //       .eq('user_id', selectedUser.id)
  //       .order('created_at', { ascending: false });
  //     return data ?? [];
  //   },
  // });

  // const { data: userProgress } = useQuery({
  //   queryKey: ['dashboard-user-progress', selectedUser?.id],
  //   enabled: !!selectedUser?.id,
  //   queryFn: async () => {
  //     const { data } = await supabase
  //       .from('user_challenge_progress')
  //       .select('*')
  //       .eq('user_id', selectedUser.id);
  //     return data ?? [];
  //   },
  // });

  const { data: userGemHistory } = useQuery({
    queryKey: ['dashboard-user-gem-history', selectedUser?.id],
    enabled: !!selectedUser?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', selectedUser.id)
        .order('created_at', { ascending: false });
      return data ?? [];
    },
  });

  const toggleProMutation = useMutation({
    mutationFn: async ({ id, is_pro }: { id: string; is_pro: boolean }) => {
      const { data: oldData } = await supabase.from('profiles').select('is_pro').eq('id', id).single();
      const { error } = await supabase.rpc('admin_set_is_pro', {
        p_user_id: id,
        p_is_pro: is_pro,
      });
      if (error) throw error;
      await logUpdate('profiles', id, { is_pro: oldData?.is_pro }, { is_pro }, {
        description: `${is_pro ? 'Enabled' : 'Disabled'} pro status`,
        action: 'toggle_pro_status',
      });
    },
    onSuccess: () => {
      toast.success('Pro status updated');
      queryClient.invalidateQueries({ queryKey: ['recent-users'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (err: any) => toast.error(err.message ?? 'Failed to update pro status'),
  });

  // One brand hue across all four tiles, differentiated by icon rather than
  // colour. These are four independent metrics, not a series — hue would encode
  // nothing here, and green/amber are reserved for state elsewhere in the app
  // (the yellow sidebar badges mean "needs attention"), so an amber "Pro Users"
  // or emerald "Free Users" chip would read as a status it isn't.
  const cards = [
    { label: 'Total Users', value: stats?.total, icon: Users },
    { label: 'Pro Users', value: stats?.pro, icon: Crown },
    { label: 'Free Users', value: stats?.free, icon: UserCheck },
    { label: '7-Day Signups', value: stats?.recent, icon: TrendingUp },
    // { label: 'Submissions', value: stats?.submissions, icon: Trophy },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Dashboard</h1>
      {/* 4 stat cards — grid-cols-4 so they fill the row instead of leaving a
          dead column at the end. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          // The gradient now comes from the shared Card surface, so the
          // decorative overlay divs this tile used to carry are gone.
          <Card key={c.label} className="transition-shadow duration-200 hover:shadow-elevate-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground">
                {c.label}
              </CardTitle>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-sm ring-1 ring-inset ring-white/25">
                <c.icon className="h-[18px] w-[18px]" />
              </span>
            </CardHeader>
            <CardContent>
              {/* Proportional figures, not tabular-nums — at display sizes
                  tabular gives every digit a `0`'s width and reads loose. */}
              <div className="text-3xl font-bold tracking-tight">{c.value ?? "–"}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Recent Signups</CardTitle>
          <CardDescription>The 10 most recent accounts to join.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Join Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-24 text-center text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : (
                recentUsers?.map((u: any) => (
                  <TableRow key={u.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedUser(u)}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {u.created_at
                        ? format(new Date(u.created_at), "MMM d, yyyy")
                        : "–"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.is_pro ? "default" : "secondary"}>
                        {u.is_pro ? "Pro" : "Free"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>User Details</SheetTitle>
          </SheetHeader>
          {selectedUser && (
            <div className="space-y-6 mt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{selectedUser.email}</p>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={selectedUser?.is_pro}
                  onCheckedChange={(v) => setPendingProChange(v)}
                />
                <Label>Pro Status</Label>
              </div>

              <AlertDialog open={pendingProChange !== null} onOpenChange={(open) => { if (!open) setPendingProChange(null); }}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Change Pro Status</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to {pendingProChange ? 'grant' : 'revoke'} Pro status for <strong>{selectedUser?.full_name || selectedUser?.tiktok_handle || selectedUser?.email}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      setSelectedUser({ ...selectedUser, is_pro: pendingProChange });
                      toggleProMutation.mutate({ id: selectedUser?.id, is_pro: pendingProChange });
                      setPendingProChange(null);
                    }}>
                      Confirm
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Tabs defaultValue="gems">
                <TabsList>
                  {/* <TabsTrigger value="submissions">Submissions</TabsTrigger>
                  <TabsTrigger value="progress">Progress</TabsTrigger> */}
                  <TabsTrigger value="gems">Gem History</TabsTrigger>
                </TabsList>

                {/* <TabsContent value="submissions" className="space-y-2">
                  {userSubmissions?.length === 0 && <p className="text-sm text-muted-foreground">No submissions</p>}
                  {userSubmissions?.map((s: any) => (
                    <div key={s?.id} className="border rounded-md p-3 text-sm">
                      <div className="flex justify-between">
                        <span>Day {s?.challenge_day ?? s?.day_number}</span>
                        <span className="text-muted-foreground">{s?.created_at && format(new Date(s?.created_at), 'MMM d')}</span>
                      </div>
                      {s?.flame_score != null && <p className="text-muted-foreground">🔥 {s?.flame_score}</p>}
                    </div>
                  ))}
                </TabsContent> */}

                {/* <TabsContent value="progress" className="space-y-2">
                  {userProgress?.length === 0 && <p className="text-sm text-muted-foreground">No progress data</p>}
                  {userProgress?.map((p: any, i: number) => (
                    <div key={i} className="border rounded-md p-3 text-sm">
                      {Object.entries(p)
                        .filter(([k]) => k !== 'id' && k !== 'user_id')
                        .map(([k, v]) => (
                          <div key={k} className="flex justify-between">
                            <span className="text-muted-foreground">{k}</span>
                            <span>{String(v ?? '')}</span>
                          </div>
                        ))}
                    </div>
                  ))}
                </TabsContent> */}

                <TabsContent value="gems" className="space-y-2">
                  {userGemHistory?.length === 0 && <p className="text-sm text-muted-foreground">No gem transactions</p>}
                  {userGemHistory?.map((t: any, i: number) => {
                    const isCredit = t.amount >= 0;
                    const typeColors: Record<string, string> = {
                      bonus: 'bg-blue-100 text-blue-800 border-blue-200',
                      challenge: 'bg-purple-100 text-purple-800 border-purple-200',
                      campaign: 'bg-orange-100 text-orange-800 border-orange-200',
                      milestone: 'bg-green-100 text-green-800 border-green-200',
                      referral: 'bg-pink-100 text-pink-800 border-pink-200',
                      deduction: 'bg-red-100 text-red-800 border-red-200',
                    };
                    const typeColor = typeColors[t.type?.toLowerCase()] ?? 'bg-gray-100 text-gray-700 border-gray-200';

                    const rawDesc = t.description || t.notes;
                    let reason: string | null = null;
                    if (rawDesc) {
                      const milestoneMatch = rawDesc.match(/^tap_milestone:(.+)$/);
                      reason = milestoneMatch
                        ? `TAP Milestone · campaign ${milestoneMatch[1]}`
                        : rawDesc;
                    }

                    return (
                      <div key={t.id ?? i} className="border rounded-md p-3 text-sm space-y-2">
                        <div className="flex justify-between items-start">
                          <span className={`font-semibold ${isCredit ? 'text-green-700' : 'text-red-600'}`}>
                            {isCredit ? '+' : ''}{t.amount} gems
                          </span>
                          <span className="text-muted-foreground text-xs">{t.created_at && format(new Date(t.created_at), 'MMM d, yyyy · h:mm a')}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs border ${typeColor}`}>{t.type ?? 'unknown'}</Badge>
                          {reason && <span className="text-muted-foreground text-xs">{reason}</span>}
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
